import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { UserService } from "@/services/userService";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Lock, Plus, Shield, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AVATAR_COLORS = ["#6d28d9", "#4f46e5", "#0e7490", "#b45309", "#15803d", "#be185d"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

interface Member {
  user_id: string;
  full_name: string;
  email: string;
}

const Settings = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });

  const fetchMembers = async () => {
    setLoading(true);
    const { data: roles } = await supabase.from("user_roles").select("user_id").eq("role", "aca_member");
    const ids = [...new Set((roles || []).map((r) => r.user_id))];
    if (ids.length === 0) {
      setMembers([]);
      setLoading(false);
      return;
    }
    const { data: profiles } = await supabase.from("profiles").select("user_id, full_name, email").in("user_id", ids);
    setMembers((profiles || []) as Member[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const addMember = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast({ title: "Missing fields", description: "Please fill in every field.", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      await UserService.create({ email: form.email, password: form.password, full_name: form.full_name, role: "aca_member" as any });
      toast({ title: "ACA member added", description: `${form.full_name} can now log in as an ACA member.` });
      setForm({ full_name: "", email: "", password: "" });
      fetchMembers();
    } catch (err: any) {
      toast({ title: "Could not add member", description: err.response?.data?.error || err.message, variant: "destructive" });
    }
    setCreating(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Settings" crumbLabel="Dashboard" crumbTo="/aca" />

        <div className="grid gap-6 lg:grid-cols-[1fr_1fr] items-start">
          {/* Add ACA member */}
          <Card className="glass-premium rounded-2xl">
            <CardContent className="p-6 md:p-8">
              <h3 className="border-l-[3px] border-primary pl-3 text-base font-bold text-foreground">Add ACA Member</h3>
              <p className="mt-1 pl-3 text-sm text-muted-foreground">Create login details for a new ACA member.</p>

              {/* autoComplete off prevents the browser from pre-filling these credential fields. */}
              <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); addMember(); }} className="mt-6 space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="h-11 pl-9 bg-muted/40"
                      autoComplete="off"
                      name="new-aca-name"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      placeholder="Jane Doe"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="h-11 pl-9 bg-muted/40"
                      type="email"
                      autoComplete="off"
                      name="new-aca-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@acaglobal.my"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="h-11 pl-9 bg-muted/40"
                      type="password"
                      autoComplete="new-password"
                      name="new-aca-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Min 6 characters"
                    />
                  </div>
                </div>

                <Button type="submit" className="gradient-warm border-0 text-white shadow-md h-11 px-6 gap-1.5" disabled={creating}>
                  <Plus className="h-4 w-4" /> {creating ? "Adding…" : "Add ACA Member"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Existing members */}
          <Card className="glass-premium rounded-2xl">
            <CardContent className="p-6 md:p-8">
              <h3 className="border-l-[3px] border-primary pl-3 text-base font-bold text-foreground">ACA Members</h3>
              <p className="mt-1 pl-3 text-sm text-muted-foreground">Everyone with ACA member access.</p>

              <div className="mt-6 divide-y divide-border overflow-hidden rounded-xl border border-border">
                {loading ? (
                  <div className="flex justify-center py-10">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : members.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    <Users className="mx-auto mb-2 h-7 w-7 text-muted-foreground/40" />
                    No ACA members yet.
                  </div>
                ) : (
                  members.map((m) => (
                    <div key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: avatarColor(m.full_name || m.email) }}>
                        {initials(m.full_name || m.email)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {m.full_name || "—"}
                          {currentUser?.id === m.user_id && <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">You</span>}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700">
                        <Shield className="h-3 w-3" /> ACA Member
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
